import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ShieldCheck, Heart } from "lucide-react";
import { tr } from "@/lib/tr";
import { useSiteAsset } from "@/lib/useSiteAsset";

const STORY_PHOTOS = [
    { slot: "landing.polaroid1", src: "https://images.unsplash.com/photo-1780329946888-0db2441b249c?crop=entropy&cs=srgb&fm=jpg&w=400&q=80", cls: "top-[18%] left-[3%] sm:left-[6%] rotate-[-6deg]" },
    { slot: "landing.polaroid2", src: "https://images.unsplash.com/photo-1670328677857-139f562944fa?crop=entropy&cs=srgb&fm=jpg&w=400&q=80", cls: "bottom-[8%] left-[8%] sm:left-[12%] rotate-[5deg]" },
    { slot: "landing.polaroid3", src: "https://images.unsplash.com/photo-1670328685183-abaa581d872e?crop=entropy&cs=srgb&fm=jpg&w=400&q=80", cls: "top-[16%] right-[4%] sm:right-[8%] rotate-[6deg]" },
    { slot: "landing.polaroid4", src: "https://images.unsplash.com/photo-1659352788045-e49643d43d4c?crop=entropy&cs=srgb&fm=jpg&w=400&q=80", cls: "bottom-[10%] right-[8%] sm:right-[13%] rotate-[-5deg]" },
];

/* Home Page reference — Section: "Every Family Has a Story Worth Preserving." (ABOUT PLATFORM) */
export function StoryIntro() {
    const navigate = useNavigate();
    const pol1 = useSiteAsset("landing.polaroid1", null);
    const pol2 = useSiteAsset("landing.polaroid2", null);
    const pol3 = useSiteAsset("landing.polaroid3", null);
    const pol4 = useSiteAsset("landing.polaroid4", null);
    const polaroidUrls = [pol1.url, pol2.url, pol3.url, pol4.url];
    return (
        <section id="every-family-story" className="relative overflow-hidden py-10 md:py-14 bg-white" data-testid="landing-story-intro">
            {/* Curved connector accents */}
            <svg className="pointer-events-none absolute inset-0 w-full h-full hidden md:block" aria-hidden="true" preserveAspectRatio="none" viewBox="0 0 1440 900">
                <path d="M120,420 C280,260 260,600 500,540" fill="none" stroke="#F87171" strokeWidth="2" strokeOpacity="0.5" />
                <path d="M1320,300 C1160,200 1200,620 940,560" fill="none" stroke="#8B5CF6" strokeWidth="2" strokeOpacity="0.5" />
            </svg>

            {/* Scattered family polaroids */}
            {STORY_PHOTOS.map((p, i) => (
                <div key={i} className={`pointer-events-none absolute hidden md:block ${p.cls}`} aria-hidden="true">
                    <div className="rounded-xl border-4 border-white shadow-[0_14px_34px_rgba(76,29,149,0.16)] overflow-hidden h-24 w-20 lg:h-28 lg:w-24 bg-[#EDE9FE]">
                        <img src={polaroidUrls[i] || p.src} alt="" loading="lazy" className="h-full w-full object-cover" />
                    </div>
                </div>
            ))}

            <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                <motion.h2
                    initial={{ opacity: 0, y: 22 }} whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.55, delay: 0.08 }}
                    className="mt-3 text-4xl md:text-5xl font-extrabold tracking-tight text-[#6D28D9] leading-[1.08]"
                >
                    Every Individual & Family Has a Story Worth Preserving.
                </motion.h2>
                <motion.p
                    initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.55, delay: 0.16 }}
                    className="mt-5 text-[15.5px] md:text-[17px] leading-relaxed text-[#4B5563]"
                >
                    Your family's story is more than names and dates — it's the memories, voices, photographs,
                    and experiences that connect generations. Build a private digital family legacy where you can
                    create your family tree, preserve cherished memories, record personal stories, and keep the
                    voices of loved ones alive.
                </motion.p>
                <motion.p
                    initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.55, delay: 0.2 }}
                    className="mt-4 text-[14px] md:text-[15px] italic text-[#6D28D9] font-medium"
                    data-testid="story-audio-tagline"
                >
                    Please listen to this Audio to understand why preservation of legacy is most important activity of your Life
                </motion.p>
                <motion.div
                    initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.55, delay: 0.24 }}
                    className="mt-3 mx-auto max-w-md rounded-xl bg-[#EDE9FE] border border-[#DDD6FE] p-3 shadow-sm"
                    data-testid="story-audio-block"
                >
                    <audio
                        controls
                        preload="metadata"
                        src="/audio-new/betgiri-sir.mp3?v=20260910c"
                        className="w-full"
                        data-testid="story-audio-el"
                    >
                        Your browser does not support the audio element.
                    </audio>
                    <p className="mt-1 text-[11.5px] text-[#6D28D9]/70 font-medium">Why Family Legacy Matters</p>
                </motion.div>

                {/* Story audio removed 2026-02-10 — production CDN was serving stale/blank for /audio/*, so we hide the section until admin re-uploads audio to a CDN-safe path. */}

                <motion.button
                    initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.5, delay: 0.24 }}
                    data-testid="story-intro-cta"
                    onClick={() => navigate("/signup")}
                    className="mt-6 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#7C3AED] to-[#6D28D9] hover:from-[#6D28D9] hover:to-[#5B21B6] text-white px-7 py-3.5 text-[15px] font-semibold shadow-[0_12px_28px_rgba(109,40,217,0.30)] transition-all hover:-translate-y-0.5"
                >
                    Start Now <ArrowRight className="h-5 w-5" />
                </motion.button>
            </div>
        </section>
    );
}

/* Home Page reference — two key benefits band. */
const BENEFITS = [
    { icon: ShieldCheck, title: "100% Private - Instant & Free", desc: "No signup. No credit card." },
    { icon: Heart, title: "Specialised Designed for Indian Families", desc: "Simple. Meaningful. User-friendly" },
];

export function BenefitsBand() {
    return (
        <section id="key-benefits" className="py-12 md:py-16 bg-white border-t border-[#F1F0FB]" data-testid="landing-benefits">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {BENEFITS.map((b, i) => (
                        <motion.div
                            key={tr(b.title)}
                            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.5, delay: i * 0.1 }}
                            className="flex items-start gap-2.5"
                            data-testid={`benefit-card-${i + 1}`}
                        >
                            <b.icon className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#EF4444]" fill="currentColor" strokeWidth={1.2} />
                            <div>
                                <h3 className="text-[17px] font-bold text-[#1F2937]">{tr(b.title)}</h3>
                                <p className="mt-0.5 text-[13.5px] leading-relaxed text-[#4B5563]">{tr(b.desc)}</p>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}
